import {MimeType} from '@coolcolduk/enum';
import {EtsyListingStateEnum} from '@coolcolduk/etsy';
import {Dropzone, useMessage} from '@coolcolduk/ui-mui';
import {Button, Stack} from '@mui/material';
import {get} from 'lodash';
import {useState} from 'react';
import {QueueJobContent} from '../../../__generated__/resolvers-types';
import EtsyListingStateSelector from '../../../components/etsy/EtsyListingStateSelector';
import {JobListButton} from '../../../components/jobList/JobListButton';
import {fetchAllListingsByShop} from '../../../hook/useGetAllListingsByShop';
import useSetEtsyBulkUpdateCsv from '../../../hook/useSetEtsyBulkUpdateCsv';
import {firebaseAuth} from '../../../setup/firebase';
import {FeatureProps} from '../../../types/FeatureObject';
import downloadListingsAsCsv from '../../../util/etsy/csv/downloadListingsAsCsvForBulkUpdate';

export interface EtsyBulkUpdateCsvProps extends FeatureProps {}

export default function EtsyBulkUpdateCsv({selectedAccount}: EtsyBulkUpdateCsvProps) {
  const [state, setState] = useState(EtsyListingStateEnum.ACTIVE);
  const {showMessage} = useMessage();
  const [csvFile, setCsvFile] = useState<File | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const {createBulkEtsyUpdateCsvJob} = useSetEtsyBulkUpdateCsv();

  return (
    <Stack direction="column" spacing={2}>
      <JobListButton content={QueueJobContent.etsyBulkUpdateCsv} />
      <EtsyListingStateSelector listingState={state} setListingState={(e) => e && setState(e)} />
      <Button
        variant="contained"
        onClick={async () => {
          setLoading(true);
          const token = await firebaseAuth.currentUser?.getIdToken();
          if (!token) {
            showMessage('Token error, please try to refresh the page or login again');
            setLoading(false);
            return;
          }
          try {
            const listings = await fetchAllListingsByShop(token, selectedAccount.id, state);
            await downloadListingsAsCsv(listings.results);
          } catch (_e) {
            showMessage('Fail downloading file');
          }

          setLoading(false);
        }}
        loading={loading}
      >
        Export CSV
      </Button>

      <Dropzone
        titleText="Import CSV"
        files={csvFile ? [csvFile] : []}
        setFiles={async (f) => {
          setCsvFile(f[0]);
        }}
        acceptedFileTypes={[MimeType.TEXT_CSV, MimeType.APPLICATION_VND_MS_EXCEL]}
        withPreview
      />

      <Button
        variant="contained"
        color="primary"
        loading={loading}
        onClick={async () => {
          if (!csvFile) return;
          const updatedListing = await createBulkEtsyUpdateCsvJob(selectedAccount.id, csvFile);
          showMessage(
            `${get(updatedListing, 'data.createQueueJob.etsyBulkUpdateCsv.items', []).length || 0} listings updated`,
          );
          setCsvFile(undefined);
        }}
        disabled={!csvFile}
      >
        Update listings
      </Button>
    </Stack>
  );
}
